import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Heart, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback images if Instagram feed fails
const FALLBACK_IMAGES = [
  "https://customer-assets.emergentagent.com/job_beauty-longevity/artifacts/wbdiqbkn_change_her_top_to_a_smooth_bla_Nano_Banana_2_37315.jpg",
  "https://customer-assets.emergentagent.com/job_beauty-longevity/artifacts/js4ake3s_i_want_a_picture_in_dusty_pink_Nano_Banana_2_81279.jpg",
  "https://customer-assets.emergentagent.com/job_beauty-longevity/artifacts/xrpwvl8z_move_the_syringe_that_the_tipi_Nano_Banana_2_91047.jpg",
  "https://customer-assets.emergentagent.com/job_beauty-longevity/artifacts/0c0819tn_make_something_in_the_same_sty_Nano_Banana_2_42394.jpg",
  "https://customer-assets.emergentagent.com/job_beauty-longevity/artifacts/rmq7icgl_i_want_this_picture_made_in_ou_Nano_Banana_2_75801.jpg",
  "https://customer-assets.emergentagent.com/job_beauty-longevity/artifacts/wbdiqbkn_change_her_top_to_a_smooth_bla_Nano_Banana_2_37315.jpg"
];

export const InstagramFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchInstagramFeed = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/instagram-feed`);
        if (response.data.posts && response.data.posts.length > 0) {
          setPosts(response.data.posts);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Failed to fetch Instagram feed:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramFeed();
  }, []);

  // Use fallback images if error or no posts
  const displayImages = error || posts.length === 0 
    ? FALLBACK_IMAGES.map((url, i) => ({
        id: `fallback-${i}`,
        media_url: url,
        permalink: 'https://instagram.com/aphrodite_med_spa',
        media_type: 'IMAGE'
      }))
    : posts;

  return (
    <section 
      data-testid="instagram-feed-section"
      className="py-24 lg:py-32 bg-chalk"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="font-cinzel text-amaranth text-xs tracking-[0.3em] mb-4">
            FOLLOW OUR JOURNEY
          </p>
          <h2 className="font-serif text-dark text-4xl lg:text-5xl mb-4">
            @aphrodite_med_spa
          </h2>
          <a
            href="https://instagram.com/aphrodite_med_spa"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-link"
            className="inline-flex items-center gap-2 text-thulian hover:text-amaranth transition-colors"
          >
            <Instagram size={20} />
            <span className="text-sm font-medium">Follow us on Instagram</span>
          </a>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-1">
            {[...Array(6)].map((_, i) => (
              <div 
                key={i} 
                className="aspect-square bg-olive/20 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Instagram Grid */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-1">
            {displayImages.slice(0, 6).map((post, index) => (
              <motion.a
                key={post.id}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`instagram-post-${index}`}
                className="group relative aspect-square overflow-hidden"
              >
                <img
                  src={post.media_type === 'VIDEO' ? post.thumbnail_url : post.media_url}
                  alt={post.caption ? post.caption.substring(0, 50) : 'Instagram post'}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-amaranth/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="flex items-center gap-4 text-white">
                    <Heart size={24} className="fill-white" />
                    <ExternalLink size={24} />
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        )}

        {/* Error/Fallback Message */}
        {error && !loading && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-dark-secondary text-sm mt-6"
          >
            Follow us on Instagram @aphrodite_med_spa for the latest updates
          </motion.p>
        )}
      </div>
    </section>
  );
};

export default InstagramFeed;
